import { CURRENCIES, ERROR_CODES, ERROR_TYPES, } from '@pf/shared';
import { z } from 'zod';
/**
 * `@pf/contracts` — wire-shape primitives shared by every endpoint schema.
 *
 * SAD §8: "REST, JSON, versioned /v1. Money is decimal strings + currency (never float)."
 * These building blocks exist so no domain schema re-declares them, and so the enum values that
 * are also meaningful at runtime elsewhere (error codes, error types, currencies) have exactly
 * one source of truth: `@pf/shared`.
 */
// ---------------------------------------------------------------------------------------------
// Currency & money
// ---------------------------------------------------------------------------------------------
/** A supported ISO-4217 currency code. Reuses `@pf/shared`'s `CURRENCIES` as the source of truth. */
export const CurrencySchema = z.enum(CURRENCIES);
/** A decimal-string monetary amount. Never a JSON number (SAD §8). */
const DECIMAL_STRING_REGEX = /^\d+(\.\d+)?$/;
/**
 * Money on the wire: `{ value: string, currency }` (SAD §8).
 *
 * `value` is validated only for *shape* here (a non-negative decimal string) — full precision,
 * scale, and range validation is `@pf/shared`'s `Money.from`, which every handler must run the
 * value through before it is trusted as a monetary amount.
 */
export const MoneySchema = z
    .object({
    value: z.string().regex(DECIMAL_STRING_REGEX, 'must be a non-negative decimal string'),
    currency: CurrencySchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Uniform error envelope (SAD §8)
// ---------------------------------------------------------------------------------------------
/** A machine-readable error code. Reuses `@pf/shared`'s `ERROR_CODES` as the source of truth. */
export const ErrorCodeSchema = z.enum(Object.values(ERROR_CODES));
/** The error category that determines HTTP status. Reuses `@pf/shared`'s `ERROR_TYPES`. */
export const ErrorTypeSchema = z.enum(ERROR_TYPES);
/**
 * The uniform error envelope every non-2xx response uses (SAD §8):
 *
 * ```
 * { "error": { "code":"OUT_OF_STOCK", "type":"conflict",
 *   "message":"No available withdrawal at that amount.",
 *   "retryable":false, "request_id":"req_..." } }
 * ```
 */
export const ErrorEnvelopeSchema = z
    .object({
    error: z
        .object({
        code: ErrorCodeSchema,
        type: ErrorTypeSchema,
        message: z.string().min(1),
        retryable: z.boolean(),
        request_id: z.string().min(1),
        details: z.record(z.string(), z.unknown()).optional(),
    })
        .strict(),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Inbound request headers (SAD §8)
// ---------------------------------------------------------------------------------------------
/** `X-PF-Participant` — the calling participant's identity. */
export const ParticipantHeaderSchema = z.string().min(1).max(128);
/** `X-PF-Timestamp` — unix seconds, as a header string. Reject if `|now - ts| > 300s` (SAD §8). */
export const TimestampHeaderSchema = z.string().regex(/^\d+$/, 'must be unix seconds');
/** `X-PF-Nonce` — single-use, blocks replay. */
export const NonceHeaderSchema = z.string().min(1).max(128);
/** `X-PF-Signature` — `v1=<hmac_sha256(...)>` (SAD §8). */
export const SignatureHeaderSchema = z.string().regex(/^v1=[0-9a-f]{64}$/, 'must be v1=<hex hmac>');
/** `Idempotency-Key` — per-participant, 24h dedupe window (SAD §8, §10). */
export const IdempotencyKeyHeaderSchema = z.string().min(1).max(255);
/**
 * The complete set of signed inbound request headers (SAD §8).
 *
 * Keyed lower-case, matching how Node/Fastify normalize incoming header names.
 */
export const InboundRequestHeadersSchema = z
    .object({
    'x-pf-participant': ParticipantHeaderSchema,
    'x-pf-timestamp': TimestampHeaderSchema,
    'x-pf-nonce': NonceHeaderSchema,
    'x-pf-signature': SignatureHeaderSchema,
    'idempotency-key': IdempotencyKeyHeaderSchema,
})
    .strict();
// ---------------------------------------------------------------------------------------------
// ULID-prefixed identifiers (SAD §12: "IDs are ULIDs")
// ---------------------------------------------------------------------------------------------
/** Crockford base32, 26 characters — the ULID body (excludes I, L, O, U). */
const ULID_BODY = '[0-9A-HJKMNP-TV-Z]{26}';
const idSchema = (prefix) => z.string().regex(new RegExp(`^${prefix}_${ULID_BODY}$`), `must be ${prefix}_<ulid>`);
/** Identifier of a deposit transaction (`txn_<ulid>`). */
export const TransactionIdSchema = idSchema('txn');
/** Identifier of a withdrawal request (`wd_<ulid>`). */
export const WithdrawalIdSchema = idSchema('wd');
/** Identifier of a submitted piece of evidence (`ev_<ulid>`). */
export const EvidenceIdSchema = idSchema('ev');
/** Identifier of an emitted domain/webhook event (`evt_<ulid>`). */
export const EventIdSchema = idSchema('evt');
/** Identifier of a manual review case (`case_<ulid>`). */
export const ReviewCaseIdSchema = idSchema('case');
/** Identifier of a console operator (`op_<ulid>`) — SAD §11 operator RBAC. */
export const OperatorIdSchema = idSchema('op');
/** Identifier of a single webhook delivery attempt record (`whd_<ulid>`) — SAD §9. */
export const DeliveryIdSchema = idSchema('whd');
// ---------------------------------------------------------------------------------------------
// Bank accounts
// ---------------------------------------------------------------------------------------------
/**
 * A bank account as carried on the wire — either a withdrawal's `receiver_account` (inbound) or
 * a reserved deposit's `pay_to` (outbound). SAD §5/§8.
 *
 * `bank` is deliberately a free-form code rather than a closed enum: SAD §12 seed data lists
 * KBANK/SCB/BBL/KTB/BAY/TMB as examples, not an exhaustive registry, and the participant onboards
 * new payee banks without a contracts release.
 */
export const BankAccountSchema = z
    .object({
    bank: z.string().min(2).max(20),
    name: z.string().min(1).max(255),
    number: z.string().min(1).max(64),
})
    .strict();
// ---------------------------------------------------------------------------------------------
// Keyset pagination (SAD §8: "reconciliation feed (keyset)")
// ---------------------------------------------------------------------------------------------
/** Common keyset-pagination query parameters used by every recon/list GET endpoint. */
export const PaginationQuerySchema = z
    .object({
    cursor: z.string().min(1).max(512).optional(),
    limit: z.coerce.number().int().min(1).max(200).optional(),
    updated_since: z.string().datetime({ offset: true }).optional(),
})
    .strict();
/**
 * Build a `{ items, next_cursor }` response schema for a keyset-paginated list endpoint.
 *
 * `next_cursor` is `null` once there is no further page — never omitted — so clients have one
 * unambiguous stop condition.
 */
export function paginatedResponseSchema(item) {
    return z
        .object({
        items: z.array(item),
        next_cursor: z.string().nullable(),
    })
        .strict();
}
// ---------------------------------------------------------------------------------------------
// An empty JSON body, for mutating endpoints with no request payload beyond headers/path
// ---------------------------------------------------------------------------------------------
/** The body of an endpoint that takes no fields — still `.strict()`, so stray fields are rejected. */
export const EmptyBodySchema = z.object({}).strict();
// ---------------------------------------------------------------------------------------------
// Deposit transaction / withdrawal request status enums (SAD §4)
// ---------------------------------------------------------------------------------------------
/** The 9 deposit-transaction states (SAD §4A). */
export const DEPOSIT_STATUSES = [
    'RESERVED',
    'AWAITING_EVIDENCE',
    'VERIFYING',
    'UNDER_REVIEW',
    'CONFIRMED',
    'CREDIT_PENDING',
    'COMPLETED',
    'EXPIRED',
    'REJECTED',
];
/** Deposit transaction status (SAD §4A state machine). */
export const DepositStatusSchema = z.enum(DEPOSIT_STATUSES);
/**
 * The 8 withdrawal-request states (SAD §4B).
 *
 * `MERGED` is the one a website added after the fact, and the only one that is not a fate the
 * withdrawer's money met: a slice whose deposit lapsed folded its amount back into the row it was
 * cut from, so the queue holds one whole request again instead of a fragment nobody can match.
 * Terminal for the slice, and NOT a funds-lock instruction of the usual kind — both locks stay
 * held, one just grows and the other closes. See `withdrawal.slice_merged` in `webhooks.ts`.
 *
 * Appended last on purpose. This enum is parsed by every participant on the recon feed, and a
 * value in the middle would renumber nothing but reads worse in every log and dashboard that
 * lists it. See 0034's header for why the split design went out of its way to avoid adding one
 * at all — that reasoning is exactly why `capability_slice_merge` gates who can ever be sent it.
 */
export const WITHDRAWAL_STATUSES = [
    'AVAILABLE',
    'RESERVED',
    'LOCKED',
    'ON_HOLD',
    'FULFILLED',
    'CANCELLED',
    'EXPIRED',
    'MERGED',
];
/** Withdrawal request status (SAD §4B state machine). */
export const WithdrawalStatusSchema = z.enum(WITHDRAWAL_STATUSES);
//# sourceMappingURL=common.js.map